import { ReactElement, useEffect, useState } from "react";
import _ from "lodash";
import { mergeClass } from "../../utils";
import { ListItem } from "./ListItem";
import { IItemListProps, IListItemProps } from "../interfaces";
import apiAgent from "../../api/apiAgent";
import { tMsg } from "../ToastNotifications";

interface IDraggableData {
  sort_id: number;
  id: number | string;
}

interface IDraggableProps {
  item: IDraggableData;
  isCard?: boolean;
  beingDragged?: boolean;
  onDragStart: React.DragEventHandler<HTMLDivElement>;
  onDragEnter: React.DragEventHandler<HTMLDivElement>;
  onDragEnd: React.DragEventHandler<HTMLDivElement>;
}

const computeNewSortId = (
  draggedIndex: number,
  staticIndex: number,
  array: IDraggableData[]
): number => {
  // If more
  if (draggedIndex > staticIndex) {
    return staticIndex === 0
      ? array[staticIndex].sort_id - 0.5
      : (array[staticIndex].sort_id + array[staticIndex - 1].sort_id) * 0.5;
  }
  // if less
  return staticIndex === array.length - 1
    ? array[staticIndex].sort_id + 0.5
    : (array[staticIndex].sort_id + array[staticIndex + 1].sort_id) * 0.5;
};

const sortItems = (itemArray: IDraggableData[]) =>
  itemArray.sort((a, b) => a.sort_id - b.sort_id);

/**
 * Sortable List of data items
 * @param param0
 * @returns
 */
const DraggableList = ({
  customListItem,
  ...passProps
}: IItemListProps & {
  customListItem?: (props: IListItemProps) => React.ReactNode;
}) => {
  const { data, modelName, isCard = false } = passProps;
  const [originalItems, setOriginalItems] = useState<IDraggableData[]>([]);
  const [sortedItems, setSortedItems] = useState<IDraggableData[]>([]);
  const [active, setActive] = useState<IDraggableData | null>(null);

  useEffect(() => {
    setOriginalItems(data);
    setSortedItems(sortItems(data));
  }, [data]);

  const handleDragStart = (e: React.DragEvent, item: IDraggableData) => {
    e.stopPropagation();
    setActive(item);
  };

  const handleDragEnter = (e: React.DragEvent, item: IDraggableData) => {
    e.preventDefault();
    if (!active) return;
    if (item.id === active.id) return;

    const indexOfStatic = sortedItems.findIndex((i) => i.id === item.id);
    const indexOfDragged = sortedItems.findIndex((i) => i.id === active.id);

    const newSortId = computeNewSortId(
      indexOfDragged,
      indexOfStatic,
      sortedItems
    );

    const draggedItem = {
      ...active,
      sort_id: newSortId,
    };
    const arrayWithoutOldItem = sortedItems.filter(
      (si) => si.id !== draggedItem.id
    );
    const arrayWithNewItem = [...arrayWithoutOldItem, draggedItem];
    setActive(draggedItem);
    setSortedItems(sortItems(arrayWithNewItem));
  };

  const handleDragEnd = (e: React.DragEvent, item: IDraggableData) => {
    if (active === null) return;
    const activeItem = active;
    updateSortId(activeItem);
    setActive(null);
  };

  const updateSortId = async (item: IDraggableData) => {
    try {
      const response = await apiAgent.DataType.update(modelName, {
        bulk_fields: [
          item,
        ],
        fields_to_set: Object.keys(item).filter(k => {return k !== "id"}),
        fields_to_match: ["id"],
      });
      if (response.status === 200) {
        setOriginalItems(sortedItems);
      }
    } catch (err) {
      tMsg.error("Error: unable to modify order");
      setSortedItems(sortItems(originalItems));
    }
  };

  return (
    <>
      {sortedItems.map((item, index) => {
        return (
          <Draggable
            key={index}
            isCard={isCard}
            item={item}
            onDragStart={(e) => handleDragStart(e, item)}
            onDragEnter={(e) => handleDragEnter(e, item)}
            onDragEnd={(e) => handleDragEnd(e, item)}
            beingDragged={active !== null && active.id === item.id}
          >
            {customListItem ? (
              customListItem({ item, ...passProps })
            ) : (
              <ListItem item={item} {...passProps} />
            )}
          </Draggable>
        );
      })}
    </>
  );
};

const Draggable = ({
  beingDragged,
  isCard,
  children,
  ...dragFunctions
}: IDraggableProps & { isCard: boolean; children: React.ReactNode }) => {
  return (
    <div
      draggable
      onDragOver={(e) => e.preventDefault()}
      {...dragFunctions}
      className={mergeClass(
        "relative bg-green-400 text-black text-center flex flex-col justify-between",
        {
          "opacity-40": beingDragged,
          "w-48 h-64": isCard,
        }
      )}
    >
      {children}
      <div
        hidden={!beingDragged}
        className="absolute top-0 h-0 w-full h-full bg-successGreen/60 pointer-events-none"
      />
    </div>
  );
};

export default DraggableList;
